const PACKAGE_ORDER = ['starter', 'explorer', 'adventurer', 'warrior'];
const CURRENT_YEAR = new Date().getFullYear();
const FITMENT_LEVELS = ['make_model', 'year', 'series', 'engine'];
const STOREFRONT_QUERY = `
  query PackageBuilderRows($type: String!, $first: Int!, $after: String) {
    metaobjects(type: $type, first: $first, after: $after, sortKey: "updated_at") {
      nodes {
        id
        handle
        fields {
          key
          value
          type
          references(first: 20) {
            nodes {
              ... on Metaobject {
                id
                type
                handle
                fields {
                  key
                  value
                  type
                  reference {
                    ... on Product {
                      title
                      handle
                      onlineStoreUrl
                      featuredImage {
                        url
                        altText
                        width
                        height
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                          currencyCode
                        }
                      }
                    }
                    ... on MediaImage {
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                  references(first: 20) {
                    nodes {
                      ... on Metaobject {
                        id
                        type
                        handle
                        fields {
                          key
                          value
                          type
                          reference {
                            ... on Product {
                              title
                              handle
                              onlineStoreUrl
                              featuredImage {
                                url
                                altText
                                width
                                height
                              }
                              priceRange {
                                minVariantPrice {
                                  amount
                                  currencyCode
                                }
                              }
                            }
                            ... on MediaImage {
                              image {
                                url
                                altText
                                width
                                height
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

class PackageBuilderSection extends HTMLElement {
  connectedCallback() {
    this.selects = {
      make_model: this.querySelector('[ref="make_model"]'),
      year: this.querySelector('[ref="year"]'),
      series: this.querySelector('[ref="series"]'),
      engine: this.querySelector('[ref="engine"]'),
    };
    this.engineField = this.querySelector('[ref="engineField"]');
    this.results = this.querySelector('[ref="results"]');
    this.status = this.querySelector('[ref="status"]');
    this.selectionSummary = this.querySelector('[ref="selectionSummary"]');
    this.buildButton = this.querySelector('[ref="buildButton"]');
    this.buildLoader = this.querySelector('[ref="buildLoader"]');
    this.clearButton = this.querySelector('[ref="clearButton"]');
    this.quoteModal = this.querySelector('[ref="quoteModal"]');
    this.quoteFormWrapper = this.querySelector('[ref="quoteFormWrapper"]');
    this.quoteCloseButton = this.querySelector('[ref="quoteCloseButton"]');
    this.quoteBackdrop = this.querySelector('[ref="quoteBackdrop"]');
    this.storefrontEndpoint = this.dataset.storefrontEndpoint || '';
    this.storefrontToken = this.dataset.storefrontToken || '';
    this.metaobjectType = this.dataset.metaobjectType || 'package_builder_package_v2';
    this.includeTestProducts = this.dataset.includeTestProducts === 'true';
    this.draftOrderEndpoint = this.dataset.draftOrderEndpoint || '';
    this.customizerFallbackImage = this.dataset.customizerFallbackImage || '';
    this.clearButtonDefaultText = this.clearButton?.textContent?.trim() || 'Clear all';
    this.makeModelOrder = this.parseMakeModelOrder(this.dataset.makeModelOrder);
    this.text = {
      customizeButtonLabel: this.dataset.customizeButtonLabel || 'Get a Quote',
      customizePackageButtonLabel: this.dataset.customizePackageButtonLabel || 'Customise Package',
      priceFallbackText: this.dataset.priceFallbackText || 'Contact us for price',
      packageFallbackLabel: this.dataset.packageFallbackLabel || 'Package',
      unavailableText: this.dataset.unavailableText || 'Unavailable',
      stockHpLabel: this.dataset.stockHpLabel || 'Stock Power',
      stockTorqueLabel: this.dataset.stockTorqueLabel || 'Stock Torque',
      newHorsepowerLabel: this.dataset.newHorsepowerLabel || 'New Power',
      newTorqueLabel: this.dataset.newTorqueLabel || 'New Torque',
      inclusionsTitle: this.dataset.inclusionsTitle || 'Inclusions',
      performanceTitle: this.dataset.performanceTitle || 'Performance Gains',
      availabilityTitle: this.dataset.availabilityTitle || 'Availability',
      emptyContentsText: this.dataset.emptyContentsText || 'Package contents will be added here.',
      notProvidedText: this.dataset.notProvidedText || 'Not provided',
      gainFallbackText:
        this.dataset.gainFallbackText || 'Performance gains vary by vehicle condition and supporting modifications.',
      expectedSpecTemplate:
        this.dataset.expectedSpecTemplate ||
        'Factory Power/Torque @ Wheels [factory_power]kW and [factory_torque]Nm. Expected [package_power]kW and [package_torque]Nm.',
      loadingMessage: this.dataset.loadingMessage || 'Loading package data...',
      missingTokenMessage: this.dataset.missingTokenMessage || 'Storefront API token or endpoint is missing.',
      loadErrorMessage:
        this.dataset.loadErrorMessage ||
        'There was a problem loading package data. Please check storefront access settings.',
      selectVehicleMessage: this.dataset.selectVehicleMessage || 'Select a vehicle to view available power packages.',
      readyMessage: this.dataset.readyMessage || 'Click Build Your Package to view available power packages.',
      selectEngineMessage: this.dataset.selectEngineMessage || 'Select an engine to view available power packages.',
      resultsMessageTemplate:
        this.dataset.resultsMessageTemplate ||
        'We have found [count] suitable power packages based on your selected engine type [engine].',
    };
    this.packageRows = [];
    this.customSelects = {};
    this.buildRequestId = 0;
    this.currentCustomizePackage = null;
    this.currentQuoteOptions = {};
    this.ownedContentIds = new Set();
    this.excludedContentIds = new Set();
    this.selectedAddonIds = new Set();
    this.mandatoryAddonIds = new Set();
    this.isCreatingDraftOrder = false;

    if (!this.handleSelectChangeBound) {
      this.handleSelectChangeBound = this.handleSelectChange.bind(this);
    }

    if (!this.handleDocumentClickBound) {
      this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    }

    if (!this.handleCustomSelectKeydownBound) {
      this.handleCustomSelectKeydownBound = this.handleCustomSelectKeydown.bind(this);
    }

    if (!this.handleBuildButtonClickBound) {
      this.handleBuildButtonClickBound = this.renderSelectedPackages.bind(this);
    }

    if (!this.handleClearButtonClickBound) {
      this.handleClearButtonClickBound = this.clearSelection.bind(this);
    }

    if (!this.handleQuoteCloseClickBound) {
      this.handleQuoteCloseClickBound = this.closeQuoteModal.bind(this);
    }

    if (!this.handleQuoteKeydownBound) {
      this.handleQuoteKeydownBound = this.handleQuoteKeydown.bind(this);
    }

    if (!this.handleGloboFormLoadedBound) {
      this.handleGloboFormLoadedBound = this.handleGloboFormLoaded.bind(this);
    }

    for (const level of FITMENT_LEVELS) {
      const select = this.selects[level];

      if (!select) {
        continue;
      }

      select.removeEventListener('change', this.handleSelectChangeBound);
      select.addEventListener('change', this.handleSelectChangeBound);
      this.setupCustomSelect(level);
    }

    document.removeEventListener('click', this.handleDocumentClickBound);
    document.addEventListener('click', this.handleDocumentClickBound);

    this.buildButton?.removeEventListener('click', this.handleBuildButtonClickBound);
    this.buildButton?.addEventListener('click', this.handleBuildButtonClickBound);

    this.clearButton?.removeEventListener('click', this.handleClearButtonClickBound);
    this.clearButton?.addEventListener('click', this.handleClearButtonClickBound);

    this.quoteCloseButton?.removeEventListener('click', this.handleQuoteCloseClickBound);
    this.quoteCloseButton?.addEventListener('click', this.handleQuoteCloseClickBound);

    this.quoteBackdrop?.removeEventListener('click', this.handleQuoteCloseClickBound);
    this.quoteBackdrop?.addEventListener('click', this.handleQuoteCloseClickBound);

    document.removeEventListener('globo.formbuilder.form.loaded', this.handleGloboFormLoadedBound);
    document.addEventListener('globo.formbuilder.form.loaded', this.handleGloboFormLoadedBound);

    this.updateResultsState();
    this.loadPackageRows();
  }

  disconnectedCallback() {
    for (const level of FITMENT_LEVELS) {
      const select = this.selects?.[level];

      if (!select || !this.handleSelectChangeBound) {
        continue;
      }

      select.removeEventListener('change', this.handleSelectChangeBound);
    }

    this.buildButton?.removeEventListener('click', this.handleBuildButtonClickBound);
    this.clearButton?.removeEventListener('click', this.handleClearButtonClickBound);
    this.quoteCloseButton?.removeEventListener('click', this.handleQuoteCloseClickBound);
    this.quoteBackdrop?.removeEventListener('click', this.handleQuoteCloseClickBound);
    document.removeEventListener('keydown', this.handleQuoteKeydownBound);
    document.removeEventListener('click', this.handleDocumentClickBound);
    document.removeEventListener('globo.formbuilder.form.loaded', this.handleGloboFormLoadedBound);
  }

  async loadPackageRows() {
    if (!this.status) {
      return;
    }

    if (!this.storefrontEndpoint || !this.storefrontToken) {
      this.status.textContent = this.text.missingTokenMessage;
      return;
    }

    this.status.textContent = this.text.loadingMessage;

    try {
      const rawRows = await this.fetchAllMetaobjects();
      this.packageRows = rawRows
        .map((row) => this.normalizeMetaobjectRow(row))
        .filter((row) => row.is_available)
        .filter((row) => this.includeTestProducts || row.is_test !== true);
      this.populateSelect('make_model');

      if (this.applyQueryParamSelection()) {
        return;
      }

      this.status.textContent = this.text.selectVehicleMessage;
    } catch (error) {
      console.error('Package builder data load failed.', error);
      this.status.textContent = this.text.loadErrorMessage;
    }
  }

  async fetchAllMetaobjects() {
    const allRows = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await fetch(this.storefrontEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query: STOREFRONT_QUERY,
          variables: {
            type: this.metaobjectType,
            first: 250,
            after,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Storefront request failed with status ${response.status}.`);
      }

      const responseJson = await response.json();

      if (Array.isArray(responseJson.errors) && responseJson.errors.length > 0) {
        throw new Error(responseJson.errors.map((item) => item.message).join(' | '));
      }

      const connection = responseJson?.data?.metaobjects;

      if (!connection) {
        throw new Error('Storefront API did not return a metaobjects connection.');
      }

      allRows.push(...(connection.nodes || []));
      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      after = connection.pageInfo?.endCursor || null;
    }

    return allRows;
  }

  normalizeMetaobjectRow(row) {
    const fields = {};

    for (const field of row.fields || []) {
      fields[field.key] = this.parseFieldValue(field);
    }

    const make = this.cleanValue(fields.make);
    const model = this.cleanValue(fields.model);
    const series = this.cleanValue(fields.series);
    const yearRange = this.cleanValue(fields.year_range);
    const packageName = this.cleanValue(fields.package_name || fields.title || '');
    const packageKey = this.handleize(fields.package_key || packageName);

    return {
      id: row.id || '',
      handle: row.handle || '',
      make,
      model,
      make_model: this.buildMakeModelLabel(make, model),
      series,
      engine: this.cleanValue(fields.engine),
      year_range: yearRange,
      years: this.expandYearRange(yearRange),
      package_name: packageName,
      package_key: packageKey,
      tier_label: packageName,
      tier_key: packageKey,
      is_available: fields.is_available !== false,
      is_test: fields.test === true,
      quote_only: fields.quote_only === true,
      package_contents: Array.isArray(fields.package_contents) ? fields.package_contents : [],
      package_content_products: this.normalizeContentItems(this.referenceNodesForField(row, 'package_contents_products')),
      package_addon_groups: this.normalizeAddonGroups(this.referenceNodesForField(row, 'package_addons')),
      factory_power_kw: this.cleanValue(fields.factory_power_kw),
      factory_torque_nm: this.cleanValue(fields.factory_torque_nm),
      package_power_kw: this.cleanValue(fields.package_power_kw),
      package_torque_nm: this.cleanValue(fields.package_torque_nm),
      notes: this.cleanValue(fields.notes),
    };
  }

  parseFieldValue(field) {
    const rawValue = field?.value ?? '';
    const fieldType = field?.type || '';

    if (fieldType.startsWith('list.')) {
      try {
        const parsed = JSON.parse(rawValue);

        if (Array.isArray(parsed)) {
          return parsed.map((item) => this.cleanValue(item)).filter(Boolean);
        }
      } catch (error) {
        return rawValue
          .split(',')
          .map((item) => this.cleanValue(item))
          .filter(Boolean);
      }
    }

    if (fieldType === 'boolean') {
      return rawValue === 'true';
    }

    return rawValue;
  }

  parseBooleanField(field) {
    const rawValue = field?.value;

    if (rawValue === true) {
      return true;
    }

    return String(rawValue || '').toLowerCase() === 'true';
  }

  handleSelectChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLSelectElement)) {
      return;
    }

    const level = target.dataset.level;
    const currentIndex = FITMENT_LEVELS.indexOf(level);

    if (currentIndex === -1) {
      return;
    }

    this.updateCustomSelect(level);

    if (level === 'engine') {
      this.clearRenderedPackages();
      this.updateSelectionStatus();
      return;
    }

    for (let index = currentIndex + 1; index < FITMENT_LEVELS.length; index += 1) {
      this.resetSelect(FITMENT_LEVELS[index]);
    }

    this.hideEngineSelect();

    if (!target.value) {
      this.clearRenderedPackages();
      this.updateSelectionStatus();
      return;
    }

    if (level === 'series') {
      this.handleSeriesSelection();
      return;
    }

    const nextLevel = FITMENT_LEVELS[currentIndex + 1];
    if (nextLevel) {
      this.populateSelect(nextLevel);
    }

    this.clearRenderedPackages();
    this.updateSelectionStatus();
  }

  resetSelect(level) {
    const select = this.selects[level];

    if (!select) {
      return;
    }

    const placeholder = this.placeholderFor(level);

    select.innerHTML = '';
    select.appendChild(this.createOption('', placeholder));
    select.value = '';
    select.disabled = true;
    this.updateCustomSelect(level);

    if (level === 'engine') {
      this.hideEngineSelect();
    }
  }

  clearSelection() {
    for (const level of FITMENT_LEVELS) {
      this.resetSelect(level);
    }

    this.populateSelect('make_model');
    this.clearRenderedPackages();
    this.updateSelectionStatus();
  }

  populateSelect(level) {
    const select = this.selects[level];

    if (!select) {
      return;
    }

    const options = this.getOptionsFor(level);

    select.innerHTML = '';
    select.appendChild(this.createOption('', this.placeholderFor(level)));

    for (const option of options) {
      select.appendChild(this.createOption(option.value, option.label));
    }

    select.disabled = options.length === 0;
    this.updateCustomSelect(level);

    if (level === 'engine' && this.engineField) {
      this.engineField.hidden = options.length < 2;
    }
  }

  getOptionsFor(level) {
    if (level === 'make_model') {
      return this.getMakeModelOptions();
    }

    if (level === 'year') {
      return this.getYearOptions();
    }

    if (level === 'series') {
      return this.getSeriesOptions();
    }

    if (level === 'engine') {
      return this.getEngineOptions();
    }

    return [];
  }

  getMakeModelOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (row.make_model) {
        optionSet.add(row.make_model);
      }
    }

    return this.sortMakeModelOptions(optionSet);
  }

  getYearOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (row.make_model !== this.selects.make_model?.value) {
        continue;
      }

      for (const year of row.years) {
        optionSet.add(year);
      }
    }

    return [...optionSet]
      .sort((firstYear, secondYear) => Number(secondYear) - Number(firstYear))
      .map((year) => ({
        label: year,
        value: year,
      }));
  }

  getSeriesOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedYear(row)) {
        continue;
      }

      if (row.series) {
        optionSet.add(row.series);
      }
    }

    return this.setToSortedOptions(optionSet);
  }

  getEngineOptions() {
    const optionSet = new Set();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedSeries(row)) {
        continue;
      }

      if (row.engine) {
        optionSet.add(row.engine);
      }
    }

    return this.setToSortedOptions(optionSet);
  }

  handleSeriesSelection() {
    const engineOptions = this.getEngineOptions();
    const engineSelect = this.selects.engine;

    if (!engineSelect) {
      this.clearRenderedPackages();
      this.updateSelectionStatus();
      return;
    }

    if (engineOptions.length === 1) {
      this.hideEngineSelect();
      engineSelect.innerHTML = '';
      engineSelect.appendChild(this.createOption('', this.placeholderFor('engine')));
      engineSelect.appendChild(this.createOption(engineOptions[0].value, engineOptions[0].label));
      engineSelect.value = engineOptions[0].value;
      engineSelect.disabled = true;
      this.updateCustomSelect('engine');
      this.clearRenderedPackages();
      this.updateSelectionStatus();
      return;
    }

    if (engineOptions.length > 1) {
      this.populateSelect('engine');
      this.clearRenderedPackages();
      this.status.textContent = this.text.selectEngineMessage;
      return;
    }

    this.hideEngineSelect();
    this.clearRenderedPackages();
    this.updateSelectionStatus();
  }

  hideEngineSelect() {
    if (this.engineField) {
      this.engineField.hidden = true;
    }

    this.closeCustomSelect('engine');
  }

  applyQueryParamSelection() {
    const selectedFitment = this.getQueryParamFitment();

    if (
      !selectedFitment.make_model &&
      !selectedFitment.year &&
      !selectedFitment.series &&
      !selectedFitment.engine
    ) {
      return false;
    }

    if (!this.setSelectValue('make_model', selectedFitment.make_model)) {
      return false;
    }

    this.populateSelect('year');

    if (!this.setSelectValue('year', selectedFitment.year)) {
      this.updateSelectionStatus();
      return true;
    }

    this.populateSelect('series');

    if (!this.setSelectValue('series', selectedFitment.series)) {
      this.updateSelectionStatus();
      return true;
    }

    this.handleSeriesSelection();

    if (!this.engineField?.hidden && !this.setSelectValue('engine', selectedFitment.engine)) {
      this.status.textContent = this.text.selectEngineMessage;
      return true;
    }

    this.renderSelectedPackages();
    return true;
  }

  getQueryParamFitment() {
    const searchParams = new URLSearchParams(window.location.search);
    const make = this.cleanValue(searchParams.get('data_make'));
    const model = this.cleanValue(searchParams.get('data_model'));
    const makeModel = this.cleanValue(searchParams.get('data_make_model')) || this.buildMakeModelLabel(make, model);

    return {
      make,
      model,
      make_model: makeModel,
      year: this.cleanValue(searchParams.get('data_year')),
      series: this.cleanValue(searchParams.get('data_series')),
      engine: this.cleanValue(searchParams.get('data_engine')),
    };
  }

  setSelectValue(level, value) {
    const select = this.selects[level];
    const cleanValue = this.cleanValue(value);

    if (!select || !cleanValue) {
      return false;
    }

    const matchingOption = [...select.options].find((option) => {
      return option.value.toLowerCase() === cleanValue.toLowerCase();
    });

    if (!matchingOption) {
      return false;
    }

    select.value = matchingOption.value;
    this.updateCustomSelect(level);
    return true;
  }

  setupCustomSelect(level) {
    const select = this.selects[level];

    if (!select || this.customSelects[level]) {
      return;
    }

    const customSelect = document.createElement('div');
    customSelect.className = 'package-builder__custom-select';
    customSelect.dataset.level = level;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'package-builder__custom-toggle';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', `${select.id}-Options`);

    const buttonText = document.createElement('span');
    buttonText.className = 'package-builder__custom-toggle-text';
    button.appendChild(buttonText);

    const optionList = document.createElement('div');
    optionList.className = 'package-builder__custom-options';
    optionList.id = button.getAttribute('aria-controls');
    optionList.role = 'listbox';
    optionList.hidden = true;

    button.addEventListener('click', () => {
      this.toggleCustomSelect(level);
    });

    customSelect.addEventListener('keydown', this.handleCustomSelectKeydownBound);
    customSelect.append(button, optionList);
    select.insertAdjacentElement('afterend', customSelect);

    this.customSelects[level] = {
      button,
      buttonText,
      optionList,
    };

    this.updateCustomSelect(level);
  }

  updateCustomSelect(level) {
    const select = this.selects[level];
    const customSelect = this.customSelects[level];

    if (!select || !customSelect) {
      return;
    }

    const selectedOption = select.options[select.selectedIndex] || select.options[0];
    customSelect.buttonText.textContent = selectedOption?.textContent || this.placeholderFor(level);
    customSelect.button.disabled = select.disabled;
    customSelect.optionList.innerHTML = '';

    for (const option of select.options) {
      if (!option.value) {
        continue;
      }

      const optionButton = document.createElement('button');
      optionButton.type = 'button';
      optionButton.className = 'package-builder__custom-option';
      optionButton.dataset.value = option.value;
      optionButton.role = 'option';
      optionButton.textContent = option.textContent;
      optionButton.setAttribute('aria-selected', option.value === select.value ? 'true' : 'false');

      optionButton.addEventListener('click', () => {
        this.selectCustomOption(level, option.value);
      });

      customSelect.optionList.appendChild(optionButton);
    }

    if (select.disabled) {
      this.closeCustomSelect(level);
    }
  }

  selectCustomOption(level, value) {
    const select = this.selects[level];

    if (!select || select.disabled) {
      return;
    }

    select.value = value;
    this.updateCustomSelect(level);
    this.closeCustomSelect(level);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  toggleCustomSelect(level) {
    const select = this.selects[level];
    const customSelect = this.customSelects[level];

    if (!select || select.disabled || !customSelect) {
      return;
    }

    const shouldOpen = customSelect.optionList.hidden;
    this.closeAllCustomSelects();

    if (shouldOpen) {
      customSelect.optionList.hidden = false;
      customSelect.button.setAttribute('aria-expanded', 'true');
    }
  }

  closeCustomSelect(level) {
    const customSelect = this.customSelects[level];

    if (!customSelect) {
      return;
    }

    customSelect.optionList.hidden = true;
    customSelect.button.setAttribute('aria-expanded', 'false');
  }

  closeAllCustomSelects() {
    for (const level of FITMENT_LEVELS) {
      this.closeCustomSelect(level);
    }
  }

  handleDocumentClick(event) {
    if (event.target.closest('.package-builder__custom-select')) {
      return;
    }

    this.closeAllCustomSelects();
  }

  handleCustomSelectKeydown(event) {
    const customSelect = event.target.closest('.package-builder__custom-select');

    if (!customSelect) {
      return;
    }

    const level = customSelect.dataset.level;
    const currentSelect = this.customSelects[level];

    if (!currentSelect) {
      return;
    }

    const optionButtons = [...currentSelect.optionList.querySelectorAll('.package-builder__custom-option')];

    if (event.key === 'Escape') {
      this.closeCustomSelect(level);
      currentSelect.button.focus();
      return;
    }

    if (event.key === 'ArrowDown' && currentSelect.optionList.hidden) {
      event.preventDefault();
      this.toggleCustomSelect(level);
      optionButtons[0]?.focus();
      return;
    }

    if (!optionButtons.includes(document.activeElement)) {
      return;
    }

    const currentIndex = optionButtons.indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      optionButtons[currentIndex + 1]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      optionButtons[currentIndex - 1]?.focus();
    }
  }

  clearRenderedPackages() {
    this.buildRequestId += 1;
    this.classList.remove('package-builder--building');

    if (this.buildButton) {
      this.buildButton.disabled = false;
    }

    if (this.buildLoader) {
      this.buildLoader.setAttribute('aria-hidden', 'true');
    }

    if (this.clearButton) {
      this.clearButton.textContent = this.clearButtonDefaultText;
    }

    if (this.results) {
      this.results.innerHTML = '';
    }

    this.currentCustomizePackage = null;
    this.ownedContentIds = new Set();
    this.excludedContentIds = new Set();
    this.selectedAddonIds = new Set();
    this.mandatoryAddonIds = new Set();
    this.classList.remove('package-builder--customizing');

    if (this.selectionSummary) {
      this.selectionSummary.hidden = true;
    }

    this.updateResultsState();
  }

  updateResultsState() {
    const hasResults = Boolean(this.results && this.results.children.length > 0);
    const hasSelectedFitment = Boolean(this.getSelectedFitment());

    this.classList.toggle('package-builder--has-results', hasResults);
    this.classList.toggle('package-builder--empty', !hasResults);
    this.classList.toggle('package-builder--fitment-selected', hasSelectedFitment);
  }

  updateSelectionStatus() {
    if (!this.status) {
      return;
    }

    if (this.getSelectedFitment()) {
      this.status.textContent = this.text.readyMessage;
      return;
    }

    this.status.textContent = this.text.selectVehicleMessage;
  }

  setToSortedOptions(optionSet) {
    return [...optionSet]
      .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue))
      .map((value) => ({
        label: value,
        value,
      }));
  }

  sortMakeModelOptions(optionSet) {
    const values = [...optionSet];

    if (this.makeModelOrder.length === 0) {
      return this.setToSortedOptions(optionSet);
    }

    const orderMap = new Map();

    for (const [index, makeModel] of this.makeModelOrder.entries()) {
      if (!orderMap.has(makeModel)) {
        orderMap.set(makeModel, index);
      }
    }

    return values
      .sort((firstValue, secondValue) => {
        const firstIndex = orderMap.has(firstValue)
          ? orderMap.get(firstValue)
          : Number.MAX_SAFE_INTEGER;
        const secondIndex = orderMap.has(secondValue)
          ? orderMap.get(secondValue)
          : Number.MAX_SAFE_INTEGER;

        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }

        return firstValue.localeCompare(secondValue);
      })
      .map((value) => ({
        label: value,
        value,
      }));
  }

  parseMakeModelOrder(rawValue) {
    return String(rawValue || '')
      .split(/\r?\n/)
      .map((value) => this.cleanValue(value))
      .filter(Boolean);
  }

  rowMatchesSelectedYear(row) {
    return row.make_model === this.selects.make_model?.value && row.years.includes(this.selects.year?.value);
  }

  rowMatchesSelectedSeries(row) {
    return this.rowMatchesSelectedYear(row) && row.series === this.selects.series?.value;
  }

  rowMatchesSelectedEngine(row) {
    const selectedEngine = this.selects.engine?.value || '';

    if (!selectedEngine) {
      return this.rowMatchesSelectedSeries(row);
    }

    return this.rowMatchesSelectedSeries(row) && row.engine === selectedEngine;
  }

  getSelectedFitment() {
    const selectedFitment = {
      make_model: this.selects.make_model?.value || '',
      year: this.selects.year?.value || '',
      series: this.selects.series?.value || '',
      engine: this.selects.engine?.value || '',
    };

    if (!selectedFitment.make_model || !selectedFitment.year || !selectedFitment.series) {
      return null;
    }

    if (!this.engineField?.hidden && !selectedFitment.engine) {
      return null;
    }

    return selectedFitment;
  }

  async renderSelectedPackages() {
    if (!this.results || !this.status || !this.selectionSummary) {
      return;
    }

    this.results.innerHTML = '';

    const selectedFitment = this.getSelectedFitment();

    if (!selectedFitment) {
      this.selectionSummary.hidden = true;
      this.status.textContent = this.text.selectVehicleMessage;
      this.updateResultsState();
      return;
    }

    this.buildRequestId += 1;
    const currentBuildRequestId = this.buildRequestId;

    this.updateResultsState();
    this.classList.add('package-builder--building');

    if (this.buildButton) {
      this.buildButton.disabled = true;
    }

    if (this.buildLoader) {
      this.buildLoader.setAttribute('aria-hidden', 'false');
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, 760);
    });

    if (currentBuildRequestId !== this.buildRequestId) {
      return;
    }

    this.classList.remove('package-builder--building');

    if (this.buildButton) {
      this.buildButton.disabled = false;
    }

    if (this.buildLoader) {
      this.buildLoader.setAttribute('aria-hidden', 'true');
    }

    this.selectionSummary.hidden = false;
    this.selectionSummary.textContent = [
      selectedFitment.make_model,
      selectedFitment.year,
      selectedFitment.series,
      selectedFitment.engine,
    ]
      .filter(Boolean)
      .join(' / ');

    if (this.clearButton) {
      this.clearButton.textContent = 'Select another vehicle';
    }

    const packageList = this.getSelectedPackageRows();
    const sortedPackages = this.sortPackages(packageList);
    const engineLabel = selectedFitment.engine || selectedFitment.series;
    this.status.textContent = this.buildResultsMessage(sortedPackages.length, engineLabel);
    this.renderPackageCards(sortedPackages);

    this.updateResultsState();
  }

  renderPackageCards(packageList) {
    if (!this.results) {
      return;
    }

    this.results.innerHTML = '';
    this.currentCustomizePackage = null;
    this.ownedContentIds = new Set();
    this.excludedContentIds = new Set();
    this.selectedAddonIds = new Set();
    this.mandatoryAddonIds = new Set();
    this.classList.remove('package-builder--customizing');

    for (const packageItem of packageList) {
      this.results.appendChild(this.buildPackageCard(packageItem));
    }
  }

  getSelectedPackageRows() {
    const packageMap = new Map();

    for (const row of this.packageRows) {
      if (!this.rowMatchesSelectedEngine(row)) {
        continue;
      }

      const packageKey = [
        row.series,
        row.engine,
        row.package_key,
        row.package_power_kw,
        row.package_torque_nm,
      ].join('|');

      if (!packageMap.has(packageKey)) {
        packageMap.set(packageKey, row);
      }
    }

    return [...packageMap.values()];
  }

  sortPackages(packages) {
    if (!Array.isArray(packages)) {
      return [];
    }

    return [...packages].sort((firstPackage, secondPackage) => {
      const firstIndex = PACKAGE_ORDER.indexOf(firstPackage?.tier_key);
      const secondIndex = PACKAGE_ORDER.indexOf(secondPackage?.tier_key);
      const safeFirstIndex = firstIndex === -1 ? PACKAGE_ORDER.length : firstIndex;
      const safeSecondIndex = secondIndex === -1 ? PACKAGE_ORDER.length : secondIndex;

      return safeFirstIndex - safeSecondIndex;
    });
  }

  placeholderFor(level) {
    const labelMap = {
      make_model: 'Select make & model',
      year: 'Select year',
      series: 'Select series',
      engine: 'Select engine',
    };

    return labelMap[level] || 'Select option';
  }

  createOption(value, label) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    return option;
  }

  buildPackageCard(packageItem) {
    const normalizedPackage = this.normalizePackageItem(packageItem);
    const displayedSpecs = this.getDisplayedPackageSpecs(normalizedPackage);
    const isCustomizable = this.isCustomizablePackage(normalizedPackage);
    const card = document.createElement('article');
    card.className = 'package-builder__card';
    card.dataset.available = normalizedPackage.is_available ? 'true' : 'false';

    const header = document.createElement('div');
    header.className = 'package-builder__card-header';

    const title = document.createElement('h3');
    title.className = 'package-builder__card-title';
    title.textContent = normalizedPackage.tier_label || normalizedPackage.package_name || this.text.packageFallbackLabel;

    const badge = document.createElement('span');
    badge.className = 'package-builder__card-badge';
    badge.textContent = this.getPackageCardPriceText(normalizedPackage);

    header.append(title, badge);
    card.appendChild(header);

    const specs = document.createElement('div');
    specs.className = 'package-builder__specs';

    specs.appendChild(this.buildSpec(this.text.stockHpLabel, normalizedPackage.factory_power_kw, 'kW'));
    specs.appendChild(this.buildSpec(this.text.stockTorqueLabel, normalizedPackage.factory_torque_nm, 'Nm'));
    specs.appendChild(this.buildSpec(this.text.newHorsepowerLabel, displayedSpecs.power, 'kW', true));
    specs.appendChild(this.buildSpec(this.text.newTorqueLabel, displayedSpecs.torque, 'Nm', true));

    card.appendChild(specs);

    const contentsWrapper = document.createElement('div');
    contentsWrapper.className = 'package-builder__content';
    const contentsTitle = document.createElement('h4');
    contentsTitle.className = 'package-builder__content-title';
    contentsTitle.textContent = this.text.inclusionsTitle;
    contentsWrapper.appendChild(contentsTitle);

    const packageContents = this.getPackageCardContents(normalizedPackage);

    if (packageContents.length > 0) {
      const contentsList = document.createElement('ul');
      contentsList.className = 'package-builder__content-list';

      for (const contentItem of packageContents) {
        const listItem = document.createElement('li');
        listItem.className = 'package-builder__content-item';

        const optionText = document.createElement('span');
        optionText.textContent = contentItem;

        listItem.appendChild(optionText);
        contentsList.appendChild(listItem);
      }

      contentsWrapper.appendChild(contentsList);
    } else if (normalizedPackage.is_available) {
      const emptyContent = document.createElement('p');
      emptyContent.className = 'package-builder__content-empty';
      emptyContent.textContent = this.text.emptyContentsText;
      contentsWrapper.appendChild(emptyContent);
    }

    card.appendChild(contentsWrapper);

    const customizeButton = document.createElement('button');
    customizeButton.type = 'button';
    customizeButton.className = 'package-builder__customize-button';

    if (isCustomizable) {
      customizeButton.classList.add('package-builder__customize-button--customize');
      customizeButton.textContent = this.text.customizePackageButtonLabel;
    } else {
      customizeButton.textContent = this.text.customizeButtonLabel;
    }

    customizeButton.disabled = !normalizedPackage.is_available;
    customizeButton.addEventListener('click', () => {
      if (isCustomizable) {
        this.openCustomizeScreen(normalizedPackage);
        return;
      }

      this.openQuoteModal(normalizedPackage);
    });
    card.appendChild(customizeButton);

    card.appendChild(this.buildPerformancePanel(normalizedPackage, displayedSpecs));

    return card;
  }

  getPackageCardPriceText(packageItem) {
    if (!packageItem?.is_available) {
      return this.text.unavailableText;
    }

    if (!this.hasStructuredPackageContents(packageItem)) {
      return this.text.priceFallbackText;
    }

    const packageTotal = this.totalItemPrices(packageItem.package_content_products || []);

    if (packageTotal <= 0) {
      return this.text.priceFallbackText;
    }

    return `From ${this.formatMoney(packageTotal, 'AUD')}`;
  }

  normalizePackageItem(packageItem) {
    const quote_only = packageItem?.quote_only === true;
    const normalizedPackage = {
      ...packageItem,
      tier_key: packageItem?.tier_key || packageItem?.package_key || '',
      tier_label: packageItem?.tier_label || packageItem?.package_name || '',
      is_available: packageItem?.is_available !== false,
      quote_only,
      notes: packageItem?.notes || '',
      package_contents: Array.isArray(packageItem?.package_contents) ? [...packageItem.package_contents] : [],
      package_content_products: !quote_only && Array.isArray(packageItem?.package_content_products)
        ? [...packageItem.package_content_products]
        : [],
      package_addon_groups: Array.isArray(packageItem?.package_addon_groups) ? [...packageItem.package_addon_groups] : [],
    };

    if (
      normalizedPackage.is_available === false &&
      normalizedPackage.notes === '' &&
      normalizedPackage.package_contents.length === 1
    ) {
      const availabilityMessage = normalizedPackage.package_contents[0] || '';

      if (availabilityMessage.toLowerCase().includes('not available')) {
        normalizedPackage.notes = availabilityMessage;
        normalizedPackage.package_contents = [];
      }
    }

    return normalizedPackage;
  }

  hasStructuredPackageContents(packageItem) {
    return Array.isArray(packageItem?.package_content_products) && packageItem.package_content_products.length > 0;
  }

  isCustomizablePackage(packageItem) {
    return packageItem?.quote_only !== true;
  }

  getPackageCardContents(packageItem) {
    if (this.hasStructuredPackageContents(packageItem)) {
      return packageItem.package_content_products
        .map((contentItem) => this.cleanValue(contentItem?.name || contentItem?.product?.title))
        .filter(Boolean);
    }

    if (!Array.isArray(packageItem?.package_contents)) {
      return [];
    }

    return packageItem.package_contents;
  }

  openCustomizeScreen(packageItem) {
    if (!this.results) {
      return;
    }

    const normalizedPackage = this.normalizePackageItem(packageItem);

    if (!this.isCustomizablePackage(normalizedPackage)) {
      this.openQuoteModal(normalizedPackage);
      return;
    }

    this.currentCustomizePackage = normalizedPackage;
    this.ownedContentIds = new Set();
    this.excludedContentIds = new Set();
    this.selectedAddonIds = new Set();
    this.mandatoryAddonIds = new Set();
    this.selectMandatoryAddons(normalizedPackage);
    this.results.innerHTML = '';
    this.results.appendChild(this.buildCustomizeScreen(normalizedPackage));
    this.classList.add('package-builder--customizing');
    this.updateResultsState();
  }

  closeCustomizeScreen() {
    this.currentCustomizePackage = null;
    this.ownedContentIds = new Set();
    this.excludedContentIds = new Set();
    this.selectedAddonIds = new Set();
    this.mandatoryAddonIds = new Set();
    this.classList.remove('package-builder--customizing');
    this.renderPackageCards(this.sortPackages(this.getSelectedPackageRows()));
    this.updateResultsState();
  }

  buildCustomizeScreen(packageItem) {
    const screen = document.createElement('section');
    screen.className = 'package-builder__customizer';
    screen.setAttribute('aria-label', `${packageItem.package_name} package customizer`);

    const heading = document.createElement('h3');
    heading.className = 'package-builder__customizer-heading';
    heading.textContent = `${packageItem.package_name || this.text.packageFallbackLabel} Package`;

    const backButton = document.createElement('button');
    backButton.type = 'button';
    backButton.className = 'package-builder__customizer-back';
    backButton.textContent = 'Back';
    backButton.addEventListener('click', () => {
      this.closeCustomizeScreen();
    });

    screen.append(heading, backButton);

    const columns = document.createElement('div');
    columns.className = 'package-builder__customizer-columns';

    const baseColumn = this.buildCustomizerColumn('Add / Remove parts', 'contents');
    const baseList = document.createElement('div');
    baseList.className = 'package-builder__customizer-list';

    for (const contentItem of packageItem.package_content_products) {
      baseList.appendChild(this.buildBaseContentCard(contentItem, packageItem));
    }

    baseColumn.appendChild(baseList);

    const addonGroups = (packageItem.package_addon_groups || []).filter((addonGroup) => {
      return Array.isArray(addonGroup.package_contents) && addonGroup.package_contents.length > 0;
    });
    const firstAddonGroup = addonGroups[0];
    const addonsColumnTitle = firstAddonGroup?.name || 'Add-ons';
    const addonsColumn = this.buildCustomizerColumn(addonsColumnTitle, 'addons');
    const addonsList = document.createElement('div');
    addonsList.className = 'package-builder__customizer-list';
    let addonItemCount = 0;

    for (const [groupIndex, addonGroup] of addonGroups.entries()) {
      const groupItems = addonGroup.package_contents || [];

      if (groupItems.length === 0) {
        continue;
      }

      if (groupIndex > 0) {
        const groupHeading = document.createElement('h5');
        groupHeading.className = 'package-builder__customizer-group-title';
        groupHeading.textContent = addonGroup.name || 'Add-ons';
        addonsList.appendChild(groupHeading);
      }

      for (const addonItem of groupItems) {
        addonsList.appendChild(this.buildAddonCard(addonItem, packageItem, addonGroup));
        addonItemCount += 1;
      }
    }

    if (addonItemCount === 0) {
      const emptyAddons = document.createElement('p');
      emptyAddons.className = 'package-builder__customizer-empty';
      emptyAddons.textContent = 'No add-ons are available for this package yet.';
      addonsList.appendChild(emptyAddons);
    }

    addonsColumn.appendChild(addonsList);

    const summaryColumn = this.buildCustomizerColumn('New Power', 'summary');
    const summary = document.createElement('div');
    summary.className = 'package-builder__customizer-summary';
    summary.dataset.summary = 'true';
    summaryColumn.appendChild(summary);

    columns.append(baseColumn, addonsColumn, summaryColumn);
    screen.appendChild(columns);

    this.updateCustomizerSummary(screen, packageItem);

    return screen;
  }

  buildCustomizerColumn(title, columnType = '') {
    const column = document.createElement('div');
    column.className = 'package-builder__customizer-column';

    if (columnType) {
      column.classList.add(`package-builder__customizer-column--${columnType}`);
    }

    const heading = document.createElement('h4');
    heading.className = 'package-builder__customizer-column-title';
    heading.textContent = title;
    column.appendChild(heading);

    return column;
  }

  buildBaseContentCard(contentItem, packageItem) {
    const card = this.buildCustomizerItemCard(contentItem, 'package-builder__customizer-item--base');
    const itemId = contentItem.id || contentItem.handle || contentItem.name;
    const isMandatory = this.isMandatoryContentItem(contentItem);
    card.dataset.mandatory = String(isMandatory);

    if (isMandatory) {
      return card;
    }

    const actionWrapper = document.createElement('div');
    actionWrapper.className = 'package-builder__base-actions';

    const ownedAction = document.createElement('button');
    ownedAction.type = 'button';
    ownedAction.className = 'package-builder__owned-toggle';
    ownedAction.textContent = 'I have this';
    ownedAction.setAttribute('aria-pressed', 'false');
    ownedAction.addEventListener('click', () => {
      const isOwned = this.ownedContentIds.has(itemId);

      if (isOwned) {
        this.ownedContentIds.delete(itemId);
      } else {
        this.ownedContentIds.add(itemId);
        this.excludedContentIds.delete(itemId);
      }

      this.updateBaseContentCardState(card, ownedAction, excludeAction, itemId);
      this.updateCustomizerSummary(this.results, packageItem);
    });

    const excludeAction = document.createElement('button');
    excludeAction.type = 'button';
    excludeAction.className = 'package-builder__exclude-toggle';
    excludeAction.textContent = 'Exclude';
    excludeAction.setAttribute('aria-label', 'Exclude from package');
    excludeAction.setAttribute('aria-pressed', 'false');
    excludeAction.addEventListener('click', () => {
      const isExcluded = this.excludedContentIds.has(itemId);

      if (isExcluded) {
        this.excludedContentIds.delete(itemId);
      } else {
        this.excludedContentIds.add(itemId);
        this.ownedContentIds.delete(itemId);
      }

      this.updateBaseContentCardState(card, ownedAction, excludeAction, itemId);
      this.updateCustomizerSummary(this.results, packageItem);
    });

    actionWrapper.append(ownedAction, excludeAction);
    card.appendChild(actionWrapper);
    return card;
  }

  updateBaseContentCardState(card, ownedAction, excludeAction, itemId) {
    const isOwned = this.ownedContentIds.has(itemId);
    const isExcluded = this.excludedContentIds.has(itemId);

    ownedAction.setAttribute('aria-pressed', String(isOwned));
    excludeAction.setAttribute('aria-pressed', String(isExcluded));
    card.dataset.owned = String(isOwned);
    card.dataset.excluded = String(isExcluded);
  }

  buildAddonCard(addonItem, packageItem, addonGroup) {
    const card = this.buildCustomizerItemCard(addonItem, 'package-builder__customizer-item--addon');
    const groupKey = this.getAddonGroupKey(addonGroup);
    const selectionKey = this.getAddonSelectionKey(addonGroup, addonItem);
    const groupSelect = this.normalizeAddonSelect(addonGroup?.select);
    const isMandatory = this.isMandatoryContentItem(addonItem);
    const groupHasMandatory = this.addonGroupHasMandatoryItem(addonGroup);
    const isLocked = isMandatory || (groupSelect === 'single' && groupHasMandatory);

    if (isMandatory) {
      this.selectedAddonIds.add(selectionKey);
      this.mandatoryAddonIds.add(selectionKey);
    }

    card.dataset.addonGroupKey = groupKey;
    card.dataset.addonSelectionKey = selectionKey;
    card.dataset.addonSelect = groupSelect;
    card.dataset.mandatory = String(isMandatory);
    card.dataset.locked = String(isLocked);
    card.dataset.selected = String(this.selectedAddonIds.has(selectionKey));

    if (isLocked) {
      const priceText = document.createElement('span');
      priceText.className = 'package-builder__addon-price';
      priceText.textContent = this.formatAddonPrice(addonItem);
      card.appendChild(priceText);
      return card;
    }

    const optionLabel = document.createElement('label');
    optionLabel.className = 'package-builder__addon-toggle';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.name = `addon-${groupKey}`;
    checkbox.checked = this.selectedAddonIds.has(selectionKey);
    checkbox.setAttribute('aria-label', `Add ${addonItem.name || 'add-on'} to package`);
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        if (groupSelect === 'single') {
          this.clearAddonGroupSelection(groupKey);
        }

        this.selectedAddonIds.add(selectionKey);
      } else {
        this.selectedAddonIds.delete(selectionKey);
      }

      this.updateAddonGroupCardStates(groupKey);
      this.updateCustomizerSummary(this.results, packageItem);
    });

    const optionText = document.createElement('span');
    optionText.textContent = this.formatAddonPrice(addonItem);

    optionLabel.append(checkbox, optionText);
    card.appendChild(optionLabel);
    return card;
  }

  buildCustomizerItemCard(item, modifierClass) {
    const card = document.createElement('article');
    card.className = `package-builder__customizer-item ${modifierClass}`;

    const media = document.createElement('div');
    media.className = 'package-builder__customizer-media';

    if (item.image?.url) {
      const image = document.createElement('img');
      image.src = item.image.url;
      image.alt = item.image.altText || item.name || '';
      image.loading = 'lazy';
      media.appendChild(image);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'package-builder__customizer-fallback-icon';
      fallback.setAttribute('aria-hidden', 'true');
      fallback.style.setProperty('--package-builder-fallback-image', `url("${this.customizerFallbackImage}")`);
      media.appendChild(fallback);
    }

    const body = document.createElement('div');
    body.className = 'package-builder__customizer-item-body';

    const title = document.createElement('h5');
    title.className = 'package-builder__customizer-item-title';
    title.textContent = item.name || item.product?.title || 'Package item';

    body.appendChild(title);

    const isAddonItem = modifierClass.includes('package-builder__customizer-item--addon');
    const itemPrice = this.formatItemPrice(item);

    if (!isAddonItem && itemPrice && itemPrice !== this.text.notProvidedText) {
      const priceText = document.createElement('p');
      priceText.className = 'package-builder__customizer-item-price';
      priceText.textContent = itemPrice;
      body.appendChild(priceText);
    }

    card.append(media, body);
    return card;
  }

  updateCustomizerSummary(root, packageItem) {
    const summary = root?.querySelector?.('[data-summary="true"]');

    if (!summary) {
      return;
    }

    summary.innerHTML = '';

    const selection = this.getCustomizerSelection(packageItem);
    const calculatedItems = [...selection.includedItems, ...selection.ownedItems, ...selection.selectedAddons];
    const selectedSpecs = this.calculateSelectedSpecs(packageItem, calculatedItems);

    const specList = document.createElement('div');
    specList.className = 'package-builder__summary-specs';
    specList.appendChild(
      this.buildSummarySpec(this.text.stockHpLabel, packageItem.factory_power_kw, 'kW', 'factory')
    );
    specList.appendChild(
      this.buildSummarySpec(this.text.stockTorqueLabel, packageItem.factory_torque_nm, 'Nm', 'factory')
    );
    specList.appendChild(this.buildSummarySpec(this.text.newHorsepowerLabel, selectedSpecs.power, 'kW'));
    specList.appendChild(this.buildSummarySpec(this.text.newTorqueLabel, selectedSpecs.torque, 'Nm'));
    summary.appendChild(specList);

    const totalText = selection.packageTotal > 0 ? this.formatMoney(selection.packageTotal, 'AUD', true) : '$0.00';

    const total = document.createElement('div');
    total.className = 'package-builder__summary-total';

    const totalValue = document.createElement('strong');
    totalValue.textContent = totalText;

    total.appendChild(totalValue);
    summary.appendChild(total);

    const buyButton = document.createElement('button');
    buyButton.type = 'button';
    buyButton.className = 'package-builder__summary-action package-builder__summary-action--buy';

    const buyPrice = document.createElement('span');
    buyPrice.className = 'package-builder__summary-action-price';
    buyPrice.textContent = totalText;

    const buySeparator = document.createElement('span');
    buySeparator.className = 'package-builder__summary-action-separator';
    buySeparator.textContent = ' - ';

    const buyLabel = document.createElement('span');
    buyLabel.textContent = 'Buy Now';

    buyButton.append(buyPrice, buySeparator, buyLabel);
    buyButton.addEventListener('click', () => {
      this.createDraftOrder(packageItem, buyButton, buyLabel);
    });

    const quoteButton = document.createElement('button');
    quoteButton.type = 'button';
    quoteButton.className = 'package-builder__summary-action package-builder__summary-action--quote';
    quoteButton.textContent = this.text.customizeButtonLabel;
    quoteButton.addEventListener('click', () => {
      this.openQuoteModal(packageItem, { includeCustomizerMessage: true });
    });

    summary.append(buyButton, quoteButton);
  }

  calculateSelectedSpecs(packageItem, selectedItems) {
    return {
      power: this.calculateSelectedSpecValue(
        packageItem.factory_power_kw,
        selectedItems,
        'package_power_kw',
        packageItem.package_power_kw
      ),
      torque: this.calculateSelectedSpecValue(
        packageItem.factory_torque_nm,
        selectedItems,
        'package_torque_nm',
        packageItem.package_torque_nm
      ),
    };
  }

  getDisplayedPackageSpecs(packageItem) {
    if (!this.isCustomizablePackage(packageItem)) {
      return {
        power: this.cleanValue(packageItem.package_power_kw),
        torque: this.cleanValue(packageItem.package_torque_nm),
      };
    }

    const defaultItems = [...packageItem.package_content_products];

    for (const addonGroup of packageItem.package_addon_groups || []) {
      const mandatoryItems = (addonGroup.package_contents || []).filter((item) => {
        return this.isMandatoryContentItem(item);
      });

      if (this.normalizeAddonSelect(addonGroup.select) === 'single') {
        if (mandatoryItems[0]) {
          defaultItems.push(mandatoryItems[0]);
        }

        continue;
      }

      defaultItems.push(...mandatoryItems);
    }

    return this.calculateSelectedSpecs(packageItem, defaultItems);
  }

  calculateSelectedSpecValue(factoryValue, selectedItems, itemField, fallbackValue) {
    const factoryRange = this.parseNumberRange(factoryValue);
    let gainMin = 0;
    let gainMax = 0;
    let hasGain = false;

    for (const item of selectedItems) {
      const itemRange = this.parseNumberRange(item?.[itemField]);

      if (!itemRange) {
        continue;
      }

      hasGain = true;
      gainMin += itemRange.min;
      gainMax += itemRange.max;
    }

    if (factoryRange) {
      if (!hasGain) {
        return this.formatNumberRange(factoryRange.min, factoryRange.max);
      }

      return this.formatNumberRange(factoryRange.min + gainMin, factoryRange.max + gainMax);
    }

    if (hasGain) {
      return this.formatNumberRange(gainMin, gainMax);
    }

    return this.cleanValue(fallbackValue);
  }

  parseNumberRange(value) {
    const numbers = this.cleanValue(value).match(/-?\d+(?:\.\d+)?/g);

    if (!numbers || numbers.length === 0) {
      return null;
    }

    const firstNumber = Number(numbers[0]);
    const secondNumber = numbers[1] ? Number(numbers[1]) : firstNumber;

    if (Number.isNaN(firstNumber) || Number.isNaN(secondNumber)) {
      return null;
    }

    return {
      min: Math.min(firstNumber, secondNumber),
      max: Math.max(firstNumber, secondNumber),
    };
  }

  formatNumberRange(minValue, maxValue) {
    const roundedMin = Math.round(minValue);
    const roundedMax = Math.round(maxValue);

    if (roundedMin === roundedMax) {
      return String(roundedMin);
    }

    return `${roundedMin}-${roundedMax}`;
  }

  buildSummarySpec(label, value, suffix, type = '') {
    const spec = document.createElement('div');
    spec.className = 'package-builder__summary-spec';

    if (type) {
      spec.classList.add(`package-builder__summary-spec--${type}`);
    }

    const specLabel = document.createElement('span');
    specLabel.textContent = label;

    const specValue = document.createElement('strong');
    specValue.textContent = value ? `${value} ${suffix}` : this.text.notProvidedText;

    spec.append(specValue, specLabel);
    return spec;
  }

  getSelectedAddonItems(packageItem) {
    const selectedAddons = [];

    for (const addonGroup of packageItem.package_addon_groups || []) {
      for (const item of addonGroup.package_contents || []) {
        const selectionKey = this.getAddonSelectionKey(addonGroup, item);

        if (this.selectedAddonIds.has(selectionKey)) {
          selectedAddons.push(item);
        }
      }
    }

    return selectedAddons;
  }

  getCustomizerSelection(packageItem) {
    const includedItems = packageItem.package_content_products.filter((item) => {
      const itemId = item.id || item.handle || item.name;

      if (this.isMandatoryContentItem(item)) {
        return true;
      }

      return !this.ownedContentIds.has(itemId) && !this.excludedContentIds.has(itemId);
    });
    const ownedItems = packageItem.package_content_products.filter((item) => {
      const itemId = item.id || item.handle || item.name;

      if (this.isMandatoryContentItem(item)) {
        return false;
      }

      return this.ownedContentIds.has(itemId) && !this.excludedContentIds.has(itemId);
    });
    const excludedItems = packageItem.package_content_products.filter((item) => {
      const itemId = item.id || item.handle || item.name;

      if (this.isMandatoryContentItem(item)) {
        return false;
      }

      return this.excludedContentIds.has(itemId);
    });
    const selectedAddons = this.getSelectedAddonItems(packageItem);
    const selectedPurchaseItems = [...includedItems, ...selectedAddons];
    const packageTotal = this.totalItemPrices(selectedPurchaseItems);

    return {
      includedItems,
      ownedItems,
      excludedItems,
      selectedAddons,
      selectedPurchaseItems,
      packageTotal,
    };
  }

  buildDraftOrderPayload(packageItem) {
    const selectedFitment = this.getSelectedFitment() || this.getQueryParamFitment();
    const selection = this.getCustomizerSelection(packageItem);
    const selectedSpecs = this.calculateSelectedSpecs(packageItem, [
      ...selection.includedItems,
      ...selection.ownedItems,
      ...selection.selectedAddons,
    ]);
    const packageName = packageItem.package_name || packageItem.tier_label || this.text.packageFallbackLabel;

    return {
      package_id: packageItem.id || '',
      package_handle: packageItem.handle || '',
      package_name: packageName,
      vehicle: {
        make_model: selectedFitment.make_model || packageItem.make_model || '',
        year: selectedFitment.year || '',
        series: selectedFitment.series || '',
        engine: selectedFitment.engine || packageItem.engine || '',
      },
      base_items: {
        selected_ids: this.itemValues(selection.includedItems, 'id'),
        selected_handles: this.itemValues(selection.includedItems, 'handle'),
        owned_ids: this.itemValues(selection.ownedItems, 'id'),
        owned_handles: this.itemValues(selection.ownedItems, 'handle'),
        excluded_ids: this.itemValues(selection.excludedItems, 'id'),
        excluded_handles: this.itemValues(selection.excludedItems, 'handle'),
      },
      addons: this.buildSelectedAddonPayload(packageItem),
      performance: {
        factory_power_kw: packageItem.factory_power_kw || '',
        factory_torque_nm: packageItem.factory_torque_nm || '',
        new_power_kw: selectedSpecs.power || '',
        new_torque_nm: selectedSpecs.torque || '',
      },
      total_price: selection.packageTotal.toFixed(2),
      currency: 'AUD',
      source_url: `${window.location.pathname}${window.location.search}`,
    };
  }

  buildSelectedAddonPayload(packageItem) {
    const selectedAddons = [];

    for (const addonGroup of packageItem.package_addon_groups || []) {
      for (const addonItem of addonGroup.package_contents || []) {
        const selectionKey = this.getAddonSelectionKey(addonGroup, addonItem);

        if (!this.selectedAddonIds.has(selectionKey)) {
          continue;
        }

        selectedAddons.push({
          group_id: addonGroup.id || '',
          group_handle: addonGroup.handle || '',
          group_name: addonGroup.name || '',
          item_id: addonItem.id || '',
          item_handle: addonItem.handle || '',
          item_name: this.getContentItemLabel(addonItem),
        });
      }
    }

    return selectedAddons;
  }

  itemValues(items, fieldName) {
    return items
      .map((item) => this.cleanValue(item?.[fieldName]))
      .filter(Boolean);
  }

  async createDraftOrder(packageItem, buyButton, buyLabel) {
    if (this.isCreatingDraftOrder) {
      return;
    }

    if (!this.draftOrderEndpoint) {
      window.alert('Draft order API endpoint is missing in theme settings.');
      return;
    }

    this.isCreatingDraftOrder = true;
    buyButton.disabled = true;
    buyButton.setAttribute('aria-busy', 'true');
    buyLabel.textContent = 'Creating order...';

    try {
      const requestPayload = this.buildDraftOrderPayload(packageItem);
      const response = await fetch(this.draftOrderEndpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });
      const responseData = await this.parseJsonResponse(response);

      if (!response.ok || responseData?.ok === false) {
        const errorMessage = responseData?.message || `Draft order request failed with status ${response.status}.`;
        throw new Error(errorMessage);
      }

      const invoiceUrl = responseData?.invoice_url || responseData?.invoiceUrl;

      if (!invoiceUrl) {
        throw new Error('Draft order API did not return an invoice URL.');
      }

      window.location.href = invoiceUrl;
    } catch (error) {
      console.error('Package builder draft order failed.', error);
      const errorMessage =
        error instanceof TypeError && error.message.toLowerCase().includes('failed to fetch')
          ? 'Unable to reach the draft order API. Please check that the API is online and allows CORS requests from this storefront.'
          : error.message || 'Unable to create draft order. Please try again.';

      window.alert(errorMessage);
      buyButton.disabled = false;
      buyButton.removeAttribute('aria-busy');
      buyLabel.textContent = 'Buy Now';
      this.isCreatingDraftOrder = false;
    }
  }

  async parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  buildCustomizerQuoteMessage(packageItem) {
    if (!packageItem || !this.hasStructuredPackageContents(packageItem)) {
      return '';
    }

    const selectedFitment = this.getSelectedFitment() || this.getQueryParamFitment();
    const selection = this.getCustomizerSelection(packageItem);
    const packageName = packageItem.package_name || packageItem.tier_label || this.text.packageFallbackLabel;
    const vehicle = [
      selectedFitment.make_model || packageItem.make_model,
      selectedFitment.year,
      selectedFitment.series,
      selectedFitment.engine,
    ]
      .filter(Boolean)
      .join(' / ');
    const totalText = selection.packageTotal > 0 ? this.formatMoney(selection.packageTotal, 'AUD') : this.text.priceFallbackText;
    const messageLines = [
      `Power Package: ${packageName}`,
      vehicle ? `Vehicle: ${vehicle}` : '',
      `Total Price: ${totalText}`,
      '',
      'Selected Package Products:',
    ].filter((line) => line !== '');

    if (selection.includedItems.length > 0) {
      for (const item of selection.includedItems) {
        messageLines.push(`- ${this.getContentItemLabel(item)} - ${this.formatItemPrice(item)}`);
      }
    } else {
      messageLines.push('- None selected');
    }

    messageLines.push('', 'Selected Add-ons:');

    if (selection.selectedAddons.length > 0) {
      for (const item of selection.selectedAddons) {
        messageLines.push(`- ${this.getContentItemLabel(item)} - ${this.formatItemPrice(item)}`);
      }
    } else {
      messageLines.push('- None selected');
    }

    if (selection.ownedItems.length > 0) {
      messageLines.push('', 'Customer Already Has:');

      for (const item of selection.ownedItems) {
        messageLines.push(`- ${this.getContentItemLabel(item)} - ${this.formatItemPrice(item)}`);
      }
    }

    if (selection.excludedItems.length > 0) {
      messageLines.push('', 'Excluded From Package:');

      for (const item of selection.excludedItems) {
        messageLines.push(`- ${this.getContentItemLabel(item)} - ${this.formatItemPrice(item)}`);
      }
    }

    return messageLines.join('\n');
  }

  selectMandatoryAddons(packageItem) {
    for (const addonGroup of packageItem.package_addon_groups || []) {
      const groupSelect = this.normalizeAddonSelect(addonGroup?.select);
      let hasMandatorySingleSelection = false;

      for (const item of addonGroup.package_contents || []) {
        if (!this.isMandatoryContentItem(item)) {
          continue;
        }

        if (groupSelect === 'single' && hasMandatorySingleSelection) {
          continue;
        }

        const groupKey = this.getAddonGroupKey(addonGroup);
        const selectionKey = this.getAddonSelectionKey(addonGroup, item);

        if (groupSelect === 'single') {
          this.clearAddonGroupSelection(groupKey);
          hasMandatorySingleSelection = true;
        }

        this.selectedAddonIds.add(selectionKey);
        this.mandatoryAddonIds.add(selectionKey);
      }
    }
  }

  clearAddonGroupSelection(groupKey) {
    for (const selectionKey of [...this.selectedAddonIds]) {
      if (this.mandatoryAddonIds.has(selectionKey)) {
        continue;
      }

      if (selectionKey.startsWith(`${groupKey}::`)) {
        this.selectedAddonIds.delete(selectionKey);
      }
    }
  }

  updateAddonGroupCardStates(groupKey) {
    const addonCards = this.results?.querySelectorAll?.('.package-builder__customizer-item--addon') || [];

    for (const addonCard of addonCards) {
      if (addonCard.dataset.addonGroupKey !== groupKey) {
        continue;
      }

      const selectionKey = addonCard.dataset.addonSelectionKey || '';
      const isMandatory = addonCard.dataset.mandatory === 'true';
      const isSelected = isMandatory || this.selectedAddonIds.has(selectionKey);
      const input = addonCard.querySelector('input');

      if (isMandatory) {
        this.selectedAddonIds.add(selectionKey);
        this.mandatoryAddonIds.add(selectionKey);
      }

      if (input) {
        input.checked = isSelected;
      }

      addonCard.dataset.selected = String(isSelected);
    }
  }

  getAddonGroupKey(addonGroup) {
    return addonGroup?.id || addonGroup?.handle || addonGroup?.name || 'addon-group';
  }

  getAddonItemKey(addonItem) {
    return addonItem?.id || addonItem?.handle || addonItem?.name || 'addon-item';
  }

  getAddonSelectionKey(addonGroup, addonItem) {
    return `${this.getAddonGroupKey(addonGroup)}::${this.getAddonItemKey(addonItem)}`;
  }

  normalizeAddonSelect(selectValue) {
    const cleanSelect = this.cleanValue(selectValue).toLowerCase();

    if (cleanSelect === 'single' || cleanSelect === 'one') {
      return 'single';
    }

    return 'multiple';
  }

  addonGroupHasMandatoryItem(addonGroup) {
    return (addonGroup?.package_contents || []).some((item) => this.isMandatoryContentItem(item));
  }

  isMandatoryContentItem(item) {
    return item?.mandatory === true;
  }

  referenceNodesForField(row, key) {
    const field = (row.fields || []).find((fieldItem) => fieldItem.key === key);
    return field?.references?.nodes || [];
  }

  normalizeAddonGroups(nodes) {
    if (!Array.isArray(nodes)) {
      return [];
    }

    return nodes.map((node) => {
      const fields = this.fieldsByKey(node);
      return {
        id: node.id || '',
        handle: node.handle || '',
        name: this.cleanValue(fields.name?.value || fields.content_name?.value),
        select: this.cleanValue(fields.select?.value || 'multiple'),
        package_contents: this.normalizeContentItems(fields.package_contents?.references?.nodes || []),
      };
    });
  }

  normalizeContentItems(nodes) {
    if (!Array.isArray(nodes)) {
      return [];
    }

    return nodes.map((node) => {
      const fields = this.fieldsByKey(node);
      const product = this.normalizeProductReference(fields.product?.reference);
      const customImage =
        this.imageFromField(fields.custom_image) ||
        this.imageFromField(fields.image) ||
        this.imageFromField(fields.media);

      return {
        id: node.id || '',
        handle: node.handle || '',
        name: this.cleanValue(fields.name?.value || fields.content_name?.value || product?.title),
        package_power_kw: this.cleanValue(fields.package_power_kw?.value),
        package_torque_nm: this.cleanValue(fields.package_torque_nm?.value),
        price_override: this.cleanValue(fields.price_override?.value),
        mandatory: this.parseBooleanField(fields.mandatory),
        product,
        image: customImage || product?.featuredImage || null,
      };
    });
  }

  imageFromField(field) {
    return field?.reference?.image || null;
  }

  fieldsByKey(node) {
    const fields = {};

    for (const field of node?.fields || []) {
      fields[field.key] = field;
    }

    return fields;
  }

  normalizeProductReference(product) {
    if (!product) {
      return null;
    }

    return {
      title: product.title || '',
      handle: product.handle || '',
      onlineStoreUrl: product.onlineStoreUrl || '',
      featuredImage: product.featuredImage || null,
      price: product.priceRange?.minVariantPrice || null,
    };
  }

  formatItemPrice(item) {
    const overridePrice = this.numberFromPrice(item?.price_override);

    if (overridePrice !== null) {
      return this.formatMoney(overridePrice, item?.product?.price?.currencyCode || 'AUD');
    }

    const productPrice = this.numberFromPrice(item?.product?.price?.amount);

    if (productPrice !== null) {
      return this.formatMoney(productPrice, item?.product?.price?.currencyCode || 'AUD');
    }

    return this.text.notProvidedText;
  }

  formatAddonPrice(item) {
    const price = this.formatItemPrice(item);

    if (!price || price === this.text.notProvidedText) {
      return price;
    }

    return `+${price}`;
  }

  getContentItemLabel(item) {
    return this.cleanValue(item?.name || item?.product?.title || 'Package item');
  }

  totalItemPrices(items) {
    let total = 0;

    for (const item of items) {
      const overridePrice = this.numberFromPrice(item?.price_override);
      const productPrice = this.numberFromPrice(item?.product?.price?.amount);
      const itemPrice = overridePrice !== null ? overridePrice : productPrice;

      if (itemPrice !== null) {
        total += itemPrice;
      }
    }

    return total;
  }

  numberFromPrice(value) {
    const cleanValue = this.cleanValue(value).replace(/[^0-9.]/g, '');

    if (!cleanValue) {
      return null;
    }

    const numberValue = Number(cleanValue);
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  formatMoney(amount, currencyCode = 'AUD', showCents = false) {
    const fractionDigits = showCents ? 2 : 0;

    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch (error) {
      return `$${amount.toLocaleString('en-AU', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })}`;
    }
  }

  getItemInitials(value) {
    return this.cleanValue(value)
      .split(/\s+/)
      .map((word) => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'GT';
  }

  buildSpec(label, value, suffix, isPackageSpec = false) {
    const spec = document.createElement('div');
    spec.className = 'package-builder__spec';

    if (isPackageSpec) {
      spec.classList.add('package-builder__spec--package');
    }

    const specLabel = document.createElement('span');
    specLabel.className = 'package-builder__spec-label';
    specLabel.textContent = label;

    const specValue = document.createElement('span');
    specValue.className = 'package-builder__spec-value';

    if (value) {
      specValue.textContent = suffix ? `${value} ${suffix}` : value;
    } else {
      specValue.textContent = this.text.notProvidedText;
    }

    spec.append(specValue, specLabel);
    return spec;
  }

  buildPerformancePanel(packageItem, displayedSpecs = null) {
    const noteWrapper = document.createElement('div');
    noteWrapper.className = 'package-builder__note';

    const noteTitle = document.createElement('h4');
    noteTitle.className = 'package-builder__note-title';
    noteTitle.textContent = packageItem.is_available ? this.text.performanceTitle : this.text.availabilityTitle;
    noteWrapper.appendChild(noteTitle);

    const noteList = document.createElement('ul');
    noteList.className = 'package-builder__note-list';

    const gainItem = document.createElement('li');
    gainItem.textContent = packageItem.notes || this.buildGainText(packageItem, displayedSpecs);
    noteList.appendChild(gainItem);

    if (packageItem.is_available) {
      const specsItem = document.createElement('li');
      specsItem.textContent = this.buildExpectedSpecText(packageItem, displayedSpecs);
      noteList.appendChild(specsItem);
    }

    noteWrapper.appendChild(noteList);
    return noteWrapper;
  }

  openQuoteModal(packageItem = null, options = {}) {
    if (!this.quoteModal) {
      return;
    }

    this.currentQuotePackage = packageItem;
    this.currentQuoteOptions = options || {};
    this.quoteFormLoadAttempts = 0;
    this.quoteModal.dataset.open = 'true';
    this.quoteModal.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.handleQuoteKeydownBound);
    this.quoteCloseButton?.focus();
    this.moveGloboFormIntoModal();
    this.prepareGloboQuoteForm();
  }

  closeQuoteModal() {
    if (!this.quoteModal) {
      return;
    }

    this.quoteModal.dataset.open = 'false';
    this.quoteModal.setAttribute('aria-hidden', 'true');
    this.currentQuoteOptions = {};
    document.removeEventListener('keydown', this.handleQuoteKeydownBound);
  }

  handleQuoteKeydown(event) {
    if (event.key === 'Escape') {
      this.closeQuoteModal();
    }
  }

  handleGloboFormLoaded() {
    if (this.quoteModal?.dataset.open === 'true') {
      this.prepareGloboQuoteForm();
    }
  }

  prepareGloboQuoteForm() {
    if (!this.currentQuotePackage) {
      return;
    }

    this.moveGloboFormIntoModal();

    const quoteForm = this.quoteModal.querySelector('.globo-formbuilder form');

    if (!quoteForm) {
      this.quoteFormLoadAttempts += 1;

      if (this.quoteFormLoadAttempts > 20) {
        return;
      }

      window.Globo?.FormBuilder?.initialize?.();
      window.setTimeout(() => {
        this.prepareGloboQuoteForm();
      }, 300);
      return;
    }

    const selectedFitment = this.getSelectedFitment() || this.getQueryParamFitment();
    const packageName = this.currentQuotePackage.package_name || this.currentQuotePackage.tier_label || '';
    const packageLabel = [selectedFitment.engine, packageName].filter(Boolean).join(' - ');
    const model = [selectedFitment.make_model || this.currentQuotePackage.make_model, selectedFitment.series]
      .filter(Boolean)
      .join(' ');

    this.fillGloboFieldByClass(quoteForm, 'globo-package', packageLabel);
    this.fillGloboFieldByClass(quoteForm, 'globo-model', model);
    this.fillGloboFieldByClass(quoteForm, 'globo-year', selectedFitment.year);
    this.clearGeneratedGloboMessage(quoteForm);

    if (this.currentQuoteOptions.includeCustomizerMessage) {
      this.fillGeneratedGloboMessage(quoteForm, this.buildCustomizerQuoteMessage(this.currentQuotePackage));
    }
  }

  moveGloboFormIntoModal() {
    if (!this.quoteFormWrapper) {
      return;
    }

    let globoForm = document.querySelector('.package-builder__globo-form');

    if (!globoForm) {
      globoForm = document.createElement('div');
      globoForm.className = 'globo-formbuilder package-builder__globo-form';
      globoForm.dataset.id = this.quoteFormWrapper.dataset.globoFormId || 'MTQ4NDgz';
      this.quoteFormWrapper.appendChild(globoForm);
      globoForm.classList.add('package-builder__globo-form--modal');
      window.Globo?.FormBuilder?.initialize?.();
      return;
    }

    if (!this.quoteFormWrapper.contains(globoForm)) {
      this.quoteFormWrapper.appendChild(globoForm);
    }

    globoForm.classList.add('package-builder__globo-form--modal');
  }

  fillGloboFieldByClass(form, className, value) {
    if (!value) {
      return;
    }

    const field = this.findGloboFieldByClass(form, className);

    if (!field) {
      return;
    }

    this.setGloboFieldValue(field, value);
  }

  findGloboFieldByClass(form, className) {
    const escapedClassName = CSS.escape(className);
    const escapedDotClassName = CSS.escape(`.${className}`);
    const selector = [
      `input.${escapedClassName}`,
      `textarea.${escapedClassName}`,
      `select.${escapedClassName}`,
      `.${escapedClassName} input`,
      `.${escapedClassName} textarea`,
      `.${escapedClassName} select`,
      `input.${escapedDotClassName}`,
      `textarea.${escapedDotClassName}`,
      `select.${escapedDotClassName}`,
      `.${escapedDotClassName} input`,
      `.${escapedDotClassName} textarea`,
      `.${escapedDotClassName} select`,
    ].join(', ');

    return form.querySelector(selector);
  }

  clearGeneratedGloboMessage(form) {
    const messageFields = Array.from(form.querySelectorAll('textarea'));
    const messageField = messageFields.find((field) => field.value.startsWith('Power Package:'));

    if (!messageField) {
      return;
    }

    this.setGloboFieldValue(messageField, '');
  }

  fillGeneratedGloboMessage(form, message) {
    if (!message) {
      return;
    }

    const messageField = form.querySelector('textarea');

    if (!messageField) {
      return;
    }

    this.setGloboFieldValue(messageField, message);
  }

  setGloboFieldValue(field, value) {
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));

    const control = field.closest('.globo-form-control');

    if (!control) {
      return;
    }

    control.querySelectorAll('.globo-label').forEach((labelElement) => {
      labelElement.classList.toggle('gfb__label__filled', Boolean(value));
    });
  }

  buildGainText(packageItem, displayedSpecs = null) {
    const factoryPower = this.numberFromRange(packageItem.factory_power_kw);
    const packagePower = this.numberFromRange(displayedSpecs?.power || packageItem.package_power_kw);

    if (!factoryPower || !packagePower || packagePower <= factoryPower) {
      return this.text.gainFallbackText;
    }

    const gainPercent = Math.round(((packagePower - factoryPower) / factoryPower) * 100);
    return `Expect around ${gainPercent}% gain in power, with final results dependent on vehicle condition.`;
  }

  buildExpectedSpecText(packageItem, displayedSpecs = null) {
    const notProvidedText = this.text.notProvidedText.toLowerCase();
    const factoryPower = packageItem.factory_power_kw || notProvidedText;
    const factoryTorque = packageItem.factory_torque_nm || notProvidedText;
    const packagePower = displayedSpecs?.power || packageItem.package_power_kw || notProvidedText;
    const packageTorque = displayedSpecs?.torque || packageItem.package_torque_nm || notProvidedText;

    return this.text.expectedSpecTemplate
      .replace('[factory_power]', factoryPower)
      .replace('[factory_torque]', factoryTorque)
      .replace('[package_power]', packagePower)
      .replace('[package_torque]', packageTorque);
  }

  buildResultsMessage(packageCount, engineLabel) {
    return this.text.resultsMessageTemplate
      .replace('[count]', packageCount)
      .replace('[engine]', engineLabel);
  }

  numberFromRange(value) {
    const numberMatches = this.cleanValue(value).match(/\d+/g) || [];

    if (numberMatches.length === 0) {
      return null;
    }

    return Number(numberMatches[numberMatches.length - 1]);
  }

  cleanValue(value) {
    return String(value || '').trim();
  }

  expandYearRange(yearRange) {
    const bounds = this.parseYearRange(yearRange);

    if (!bounds) {
      return [];
    }

    const years = [];

    for (let year = bounds.start; year <= bounds.end; year += 1) {
      years.push(String(year));
    }

    return years;
  }

  parseYearRange(yearRange) {
    const cleanYearRange = this.cleanValue(yearRange);
    const yearMatches = cleanYearRange.match(/\d{4}/g) || [];

    if (yearMatches.length === 0) {
      return null;
    }

    const start = Number(yearMatches[0]);
    let end = start;

    if (cleanYearRange.toLowerCase().includes('current')) {
      end = CURRENT_YEAR;
    } else if (yearMatches.length > 1) {
      end = Number(yearMatches[yearMatches.length - 1]);
    }

    if (Number.isNaN(start) || Number.isNaN(end)) {
      return null;
    }

    if (end < start) {
      return {
        start,
        end: start,
      };
    }

    return {
      start,
      end,
    };
  }

  buildMakeModelLabel(make, model) {
    const cleanMake = this.cleanValue(make);
    const cleanModel = this.cleanValue(model);

    return [cleanMake, cleanModel].filter(Boolean).join(' ');
  }

  handleize(value) {
    return this.cleanValue(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}

if (!customElements.get('package-builder-section')) {
  customElements.define('package-builder-section', PackageBuilderSection);
}
